/**
 * UI-3a Task 5 — CampaignConfigTab.
 *
 * The "Config" tab of the Campaign drawer (spec §4.3). Renders a free-form
 * JSON editor textarea for the campaign's `config` jsonb field.
 *
 * Why useState (not ZodForm)
 * --------------------------
 * The raw textarea text is genuine UI state: the user may type intermediate
 * invalid JSON that is not yet parseable. A ZodForm would try to validate a
 * structured field; this is a raw JSON editor, so plan §0.H's ZodForm rule
 * does not apply. Plain useState is correct here.
 *
 * Parent-validity observation — Option A: onValidityChange
 * ---------------------------------------------------------
 * `onChange` is called ONLY when the JSON is valid (so the parent always
 * receives a well-formed Record). To let the parent disable its Save button
 * while the textarea contains invalid JSON, an optional `onValidityChange`
 * callback is provided. The parent can use this to gate the Save action.
 *
 *   - `onChange(parsed)`           — called only on successful parse
 *   - `onValidityChange(valid)`    — called every keystroke with the validity state
 *
 * Empty-textarea behaviour
 * ------------------------
 * `JSON.parse('')` throws. An empty or whitespace-only textarea is treated as
 * the empty object `{}` (valid), which is the most useful default for a
 * campaign config field. This avoids forcing the user to type `{}` explicitly.
 */
import { useState, type JSX } from 'react'
import { InputTextarea } from 'primereact/inputtextarea'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface CampaignConfigTabProps {
  /** Current config object — rendered as pretty-printed JSON on mount. */
  value: Record<string, unknown>
  /**
   * Called with the newly parsed object when the textarea contains valid JSON.
   * NOT called while the text is syntactically invalid.
   */
  onChange: (next: Record<string, unknown>) => void
  /**
   * Optional — called every time the validity state changes (or on every
   * keystroke). The parent (CampaignDrawer) uses this to disable Save while
   * JSON is invalid.
   */
  onValidityChange?: (valid: boolean) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CampaignConfigTab({
  value,
  onChange,
  onValidityChange,
}: CampaignConfigTabProps): JSX.Element {
  const [text, setText] = useState<string>(JSON.stringify(value, null, 2))
  const [error, setError] = useState<string | null>(null)

  function handleChange(next: string): void {
    setText(next)

    // Treat empty / whitespace-only input as `{}` (valid empty config).
    const trimmed = next.trim()
    if (trimmed === '') {
      setError(null)
      onValidityChange?.(true)
      onChange({})
      return
    }

    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>
      setError(null)
      onValidityChange?.(true)
      onChange(parsed)
    } catch (e) {
      setError((e as Error).message)
      onValidityChange?.(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium" htmlFor="campaign-config-textarea">
        Config (JSON)
      </label>
      <InputTextarea
        id="campaign-config-textarea"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        rows={12}
        className="w-full font-mono text-sm"
        aria-label="Campaign config JSON"
        aria-invalid={error != null ? 'true' : 'false'}
        aria-describedby={error != null ? 'campaign-config-error' : undefined}
      />
      {error != null && (
        <p
          id="campaign-config-error"
          role="alert"
          className="text-red-600 text-sm"
        >
          {error}
        </p>
      )}
    </div>
  )
}
